import MessageReceipt, {conversationReceipt} from '../../components/MessageReceipt';
import React, { useEffect, useState, useCallback, useRef } from "react";
import {conversationDelivery} from './offerDelivery';
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Box, Button, Flex, Input, Text, useToast, Spinner, Badge, HStack,
  Tabs, TabList, Tab, Divider, Textarea,
} from "@chakra-ui/react";
import {
  getInbox, getConversation, getTakeoverStatus, takeoverConversation,
  releaseConversation, sendMessage,
} from "../../utils/apis/agentApi";

import InboxFilterControls from "./InboxFilterControls";
import { InboxFilters } from "../../utils/apis/agentApi";
import ConversationExport from "./ConversationExport";
import ConversationIssues from "./ConversationIssues";
import RegistrationStatus from "./RegistrationStatus";
import ConversationImage from "./ConversationImage";
import ConversationMessages from "./ConversationMessages";
import NewWhatsAppChat from './NewWhatsAppChat';
import ChatAdminActions from './ChatAdminActions';
import ConversationProRequest from './ConversationProRequest';
import ConversationAccountLinks from './ConversationAccountLinks';

const REFRESH_MS = 10000; // auto-refresh so new customer messages appear
// Re-check the takeover lock every Nth refresh rather than every one. It only
// changes when a person clicks, so ~every minute is plenty to notice a colleague.
const TAKEOVER_EVERY = 6;

const TABS = ["all", "attention", "applications", "ratings"];
/** Every inbound message was labelled "Customer", including a provider's. Half
 *  the threads in this inbox are providers, so support read a provider's own
 *  words as if the customer had said them. The label now follows the account. */
const partyLabel = (party?: string | null) =>
  party === "provider" ? "Provider" : party === "customer" ? "Customer" : "Them";

/** Times were not shown at all, so a thread gave no sense of how long anyone
 *  had been waiting between messages. */
const atTime = (ts?: string | null, includeYear = false) => {
  if (!ts) return "";
  const d = new Date(ts);
  if (isNaN(d.getTime())) return "";
  return d.toLocaleString("en-GB", {
    timeZone: "Africa/Lagos",
    ...(includeYear ? {year: "numeric" as const} : {}),
    day: "numeric", month: "short", hour: "2-digit", minute: "2-digit",
  });
};

const KIND_STYLE: Record<string, { align: string; bg: string; label: string }> = {
  user: { align: "flex-start", bg: "admin.subtle", label: "Customer" },
  agent: { align: "flex-end", bg: "admin.selected", label: "Agent" },
  human: { align: "flex-end", bg: "admin.successBg", label: "You" },
  event: { align: "center", bg: "admin.eventBg", label: "" },
};

/** Agent conversations: inbox on the left, the thread + human takeover + direct
 *  send on the right. Replaces the agent's HTML inbox. */
const Conversations = () => {
  const [filters, setFilters] = useState<InboxFilters>({});
  const [tab, setTab] = useState("all");
  const [inbox, setInbox] = useState<any[]>([]);
  const [q, setQ] = useState("");
  const [active, setActive] = useState<string | null>(null);
  const [thread, setThread] = useState<any>(null);
  const [takeover, setTakeover] = useState<any | null>(null); // the active lock, or null
  const [loadingInbox, setLoadingInbox] = useState(false);
  const [loadingThread, setLoadingThread] = useState(false);
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const draft = active ? drafts[active] || "" : "";
  const activeRef = useRef<string | null>(null);
  const threadRequest = useRef(0);
  const inboxRequest = useRef(0);
  const inboxPending = useRef<{request: number; filter: string} | null>(null);
  const threadPending = useRef<{request: number; phone: string} | null>(null);
  const inboxFilter = useRef("");
  inboxFilter.current = JSON.stringify([tab, q, filters]);
  const pendingSends = useRef(new Set<string>());
  const pendingTakeovers = useRef(new Set<string>());
  const [sendingPhones, setSendingPhones] = useState<string[]>([]);
  const [changingTakeovers, setChangingTakeovers] = useState<string[]>([]);
  useEffect(() => () => {
    activeRef.current = null;
    threadRequest.current += 1;
    inboxRequest.current += 1;
  }, []);
  const toast = useToast();
  const [newWhatsApp,setNewWhatsApp] = useState(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams(); // ?phone= from a review deep link

  // `quiet` = the 10s background poll: no spinner, no error toast, so the list
  // updates silently underneath you instead of flashing a loader.
  const loadInbox = useCallback(async (quiet = false) => {
    const filter = JSON.stringify([tab, q, filters]);
    if (quiet && inboxPending.current?.filter === filter) return;
    const request = ++inboxRequest.current;
    inboxPending.current = {request, filter};
    if (!quiet) setLoadingInbox(true);
    const res = await getInbox(tab, q || undefined, 100, filters);
    if (inboxPending.current?.request === request) inboxPending.current = null;
    if (request !== inboxRequest.current || filter !== inboxFilter.current) return;
    if (res.ok && Array.isArray(res.data)) setInbox(res.data);
    else if (!quiet) toast({ title: res.status === 502 ? "Agent unreachable" : "Failed to load inbox",
      status: "error", duration: 3000, isClosable: true });
    setLoadingInbox(false);
  }, [tab, q, filters, toast]);

  useEffect(() => { loadInbox(); }, [loadInbox]);

  /** Thread + takeover state. `quiet` skips spinners so the auto-refresh
   *  doesn't flicker the pane while you're reading it. */
  const openThread = useCallback(async (
    phone: string, quiet = false, withTakeover = true,
  ) => {
    if (quiet && activeRef.current !== phone) return;
    const request = ++threadRequest.current;
    threadPending.current = {request, phone};
    if (!quiet) { activeRef.current = phone; setActive(phone); setLoadingThread(true); setThread(null); setTakeover(null); }
    // The takeover lock is fetched on open and only occasionally on refresh:
    // it changes when someone clicks, not on its own. `withTakeover` is what
    // the background poll turns off for most cycles.
    const [t, tk] = await Promise.all([
      getConversation(phone),
      withTakeover ? getTakeoverStatus(phone) : Promise.resolve(null),
    ]);
    if (threadPending.current?.request === request) threadPending.current = null;
    if (activeRef.current !== phone || request !== threadRequest.current) return;
    if (t.ok && t.data) setThread(t.data);
    else if (!quiet) toast({ title: "Failed to load conversation", status: "error", duration: 3000 });
    if (tk && tk.ok && tk.data) setTakeover(tk.data.takeover ?? null);
    setLoadingThread(false);
  }, [toast]);

  // Deep link: a support "Review:" link is /conversations?phone=+234... — open
  // that thread straight away (once). The browser already decoded %2B to +.
  useEffect(() => {
    const phone = searchParams.get("phone");
    const pro = phone?.match(/^pro:([a-f0-9]{24})$/);
    if (pro) {navigate(`/pureworker-pro?request=${pro[1]}`, {replace: true}); return;}
    // Approved WhatsApp URL buttons retain their old fixed /conversations base.
    // New alerts carry an issue reference instead of a customer phone.
    const support = phone?.match(/^support:([a-fA-F0-9]{24})$/);
    if (support) {navigate(`/chat?user=${support[1]}`, {replace: true}); return;}
    const review = phone?.match(/^review(?::(flag|case):([1-9]\d*))?$/);
    if (review) {
      navigate(`/flag-review${review[1] ? `?${review[1]}=${review[2]}` : ''}`, {replace: true});
      return;
    }
    if (phone) openThread(phone);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  // Poll so a new customer message shows up without a manual reload.
  //
  // Two things this deliberately does NOT do, because it used to do both:
  //
  // 1. Poll while nobody is looking. A support tab left open in a background
  //    window ran the full cycle every 10s all day. At three requests a cycle
  //    that is ~1,080 requests an hour, each pulling 100 inbox rows, for a
  //    screen no one can see. `document.hidden` costs nothing and removes it.
  // 2. Re-check the takeover lock every cycle. It changes when an operator
  //    clicks Take over or Release, which this component already knows about,
  //    or when a colleague does — worth noticing, but not every ten seconds.
  //
  // A hidden tab refreshes IMMEDIATELY on becoming visible again, so the saving
  // is invisible to the person using it: they never look at stale data.
  useEffect(() => {
    let cycle = 0;
    const tick = () => {
      if (typeof document !== "undefined" && document.hidden) return;
      cycle += 1;
      loadInbox(true);
      if (active && threadPending.current?.phone !== active) openThread(active, true, cycle % TAKEOVER_EVERY === 0);
    };
    const id = setInterval(tick, REFRESH_MS);

    const onVisible = () => {
      if (!document.hidden) tick();   // catch up the moment they come back
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      clearInterval(id);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [active, loadInbox, openThread]);

  const doTakeover = async () => {
    const phone = activeRef.current;
    if (!phone || pendingTakeovers.current.has(phone)) return;
    const isActive = !!takeover;
    pendingTakeovers.current.add(phone);
    setChangingTakeovers(Array.from(pendingTakeovers.current));
    try {
      const res = isActive ? await releaseConversation(phone) : await takeoverConversation(phone);
      if (res.ok) {
        toast({ title: isActive ? "Released back to the agent" : "You have taken over",
          description: isActive ? undefined : "The AI is paused until release or the takeover timer expires.",
          status: "success", duration: 3000 });
        await openThread(phone, true);
      } else toast({ title: "Could not update takeover", status: "error", duration: 3000 });
    } finally {
      pendingTakeovers.current.delete(phone);
      setChangingTakeovers(Array.from(pendingTakeovers.current));
    }
  };

  const send = async () => {
    const phone = activeRef.current;
    const original = draft;
    if (!phone || phone !== active || !original.trim() || pendingSends.current.has(phone)) return;
    pendingSends.current.add(phone);
    setSendingPhones(Array.from(pendingSends.current));
    try {
      const res = await sendMessage(phone, original.trim());
      if (res.ok) {
        // Preserve text typed while this send was in flight, including drafts in other chats.
        setDrafts(previous => previous[phone] === original ? {...previous, [phone]: ""} : previous);
        toast({ title: "Sent", description: phone, status: "success", duration: 2000 });
        await openThread(phone, true);
      } else if (res.status === 409) toast({ title: "24h window closed", description: "Can't send free-form now. Your draft is kept.", status: "warning", duration: 4000 });
      else toast({ title: "Send failed", description: "Your draft is kept. Please try again.", status: "error", duration: 3000 });
    } finally {
      pendingSends.current.delete(phone);
      setSendingPhones(Array.from(pendingSends.current));
    }
  };

  const closeThread = () => {
    activeRef.current = null;
    threadRequest.current += 1;
    threadPending.current = null;
    setActive(null); setThread(null); setTakeover(null); setLoadingThread(false);
    if (searchParams.has('phone')) navigate('/conversations', {replace: true});
  };

  return (
    <Flex p={3} gap={3} height="calc(100dvh - 90px)" minH="320px" direction={{base: "column", lg: "row"}} minW={0}>
      {/* Inbox */}
      <Box w={{base: "100%", lg: "340px"}} flex={{base: "1", lg: "0 0 340px"}} minH={0} borderWidth="1px" borderRadius="md" overflow="hidden" display={{base: active ? "none" : "flex", lg: "flex"}} flexDirection="column" aria-label="Conversation list">
        <Box p={2} flexShrink={0}>
          <Button size="sm" colorScheme="purple" mb={3} w="full" onClick={()=>setNewWhatsApp(true)}>+ New WhatsApp chat</Button>
          {newWhatsApp && <NewWhatsAppChat onClose={()=>setNewWhatsApp(false)} onSent={phone=>{
            setNewWhatsApp(false);void openThread(phone);void loadInbox(true);
            toast({title:'WhatsApp accepted the message',description:'Delivery and reading are not yet confirmed.',status:'success',duration:5000,isClosable:true});
          }}/>}
          <Text fontSize="sm" color="admin.muted" mb={2}>Agent conversations · Mobile app, WhatsApp and website</Text>
          <HStack align="start"><ConversationExport /><InboxFilterControls value={filters} onApply={setFilters} /></HStack>
          <Tabs size="sm" index={TABS.indexOf(tab)} onChange={(i) => setTab(TABS[i])} colorScheme="purple">
            <TabList>{TABS.map((t) => <Tab key={t} textTransform="capitalize">{t}</Tab>)}</TabList>
          </Tabs>
          <Input mt={2} size="sm" placeholder="Search name, email or phone" value={q}
            onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && loadInbox()} />
        </Box>
        <Divider />
        <Box overflowY="auto" flex="1" minH={0} tabIndex={0} aria-label="Scrollable chats" sx={{WebkitOverflowScrolling: "touch", overscrollBehavior: "contain"}}>
          {loadingInbox ? <Spinner m={4} /> : inbox.length === 0 ? (
            <Text p={4} color="admin.muted">Empty.</Text>
          ) : inbox.map((c) => (
            <Box key={c.phone_number} p={3} cursor="pointer" role="button" tabIndex={0}
              onKeyDown={e => {if (e.key === "Enter" || e.key === " ") {e.preventDefault(); void openThread(c.phone_number);}}}
              backgroundColor={active === c.phone_number ? "admin.selected" : "admin.surface"}
              _hover={{ backgroundColor: "admin.subtle" }} onClick={() => openThread(c.phone_number)} borderBottomWidth="1px">
              <Flex justify="space-between">
                <Text fontWeight="bold" fontSize="sm">{c.display_name || c.phone_number}</Text>
                {c.role && <Badge fontSize="0.6em">{c.role}</Badge>}
              </Flex>
              <Text fontSize="xs" color="admin.muted" noOfLines={1}>{c.contact_phone || c.phone_number}{c.email ? ` · ${c.email}` : ""}</Text>
              <Text fontSize="xs" color="admin.muted" noOfLines={1}>{c.last_message_preview}</Text>
              {atTime(c.last_message_at, true) && (
                <Text as="time" dateTime={c.last_message_at} display="block" mt={1} fontSize="xs"
                  color="admin.muted" title="Last message time (Lagos)">
                  {atTime(c.last_message_at, true)} (Lagos)
                </Text>
              )}
              <HStack mt={1} spacing={1}>
                {c.pro_interest && <Badge colorScheme="purple" fontSize="0.6em">Pureworker Pro</Badge>}
                {c.customer_outcome && <Badge colorScheme="green" fontSize="0.6em">{({recommended: "SPs recommended", communicated: "Communication happened", completed: "Job completed"} as Record<string, string>)[c.customer_outcome]}</Badge>}
                {c.registration_status === "unregistered" && <Badge colorScheme="orange" fontSize="0.6em">Not registered{c.registration_progress === "awaiting_details" ? " - awaiting details" : ""}</Badge>}
                {(c.unresolved_flag_reasons || []).map((r: string) => (
                  <Badge key={r} colorScheme="orange" fontSize="0.6em">{r}</Badge>
                ))}
                {c.has_new_application && <Badge colorScheme="green" fontSize="0.6em">new application</Badge>}
                {c.low_rating && <Badge colorScheme="red" fontSize="0.6em">low rating</Badge>}
              </HStack>
            </Box>
          ))}
        </Box>
      </Box>

      {/* Thread */}
      <Box flex="1" minW={0} minH={0} borderWidth="1px" borderRadius="md" display={{base: active ? "flex" : "none", lg: "flex"}} flexDirection="column">
        <Flex px={3} py={2} flexShrink={0} borderBottomWidth="1px" justify="space-between" align="center">
          <Button display={{base: "inline-flex", lg: "none"}} size="sm" variant="ghost" onClick={closeThread}>Back to chats</Button>
          <Button size="sm" variant="ghost" aria-label="Close conversation" onClick={closeThread}>�</Button>
        </Flex>
        {!active ? (
          <Flex flex="1" align="center" justify="center"><Text color="admin.muted">Select a conversation</Text></Flex>
        ) : loadingThread ? <Spinner m={6} /> : thread ? (
          <>
            <Flex p={3} borderBottomWidth="1px" justify="space-between" align="center" wrap="wrap" gap={2}>
              <Box>
                <Text fontWeight="bold" overflowWrap="anywhere">{thread.display_name || thread.phone_number}</Text>
                <ConversationAccountLinks key={active} identity={active} userId={thread.user_id}/>
                <Text fontSize="xs" color="admin.muted">
                  {thread.phone_number} · {partyLabel(thread?.party)} ·{" "}
                  {thread.language || "?"} ·{" "}
                  {thread.opted_in ? "opted in" : "not opted in"}
                </Text>
              </Box>
              <HStack flexWrap="wrap">
                <Badge colorScheme="purple" px={2} py={1} borderRadius="md"
                  title="Channel recorded for the latest customer message or contact request">
                  {({ whatsapp: "WhatsApp", web: "Website", mobile: "Mobile app" } as Record<string, string>)[thread.channel] || "Channel unknown"}
                </Badge>
                {takeover && (
                  <Badge colorScheme="green" px={2} py={1} borderRadius="md">
                    Handled by {takeover.by || "support"}
                    {takeover.minutes_left != null ? ` · ${takeover.minutes_left}m left` : ""}
                  </Badge>
                )}
                <Button size="sm" colorScheme={takeover ? "green" : "purple"}
                  isLoading={changingTakeovers.includes(active)} backgroundColor={takeover ? undefined : "#88077b"} onClick={doTakeover}>
                  {takeover ? "Release to agent" : "Take over"}
                </Button>
              </HStack>
            </Flex>
            <ChatAdminActions key={"ChatAdminActions:" + active} phone={active} userId={thread.user_id} onRefresh={()=>{void openThread(active, true);void loadInbox(true);}}>{thread.pro_interest && <ConversationProRequest request={thread.pro_interest} phone={active}/>}</ChatAdminActions>
            <ConversationIssues key={"ConversationIssues:" + active} phone={active} flags={thread.flags || []} onResolved={issue => {
              if (activeRef.current !== active) return;
              threadRequest.current += 1; // A poll started before resolution cannot restore an open flag.
              setThread((current: any) => current?.phone_number === active ? {
                ...current, flags: (current.flags || []).map((f: any) => f.id === issue.id ? issue : f),
              } : current);
              loadInbox(true);
            }} />
            <RegistrationStatus registration={thread.registration} />
            {takeover && (
              <Box px={4} py={2} bg="admin.successBg" borderBottomWidth="1px">
                <Text fontSize="xs" color="admin.successText">
                  The AI is paused until this chat is released or the takeover timer expires. Messages sent here remain in its conversation history.
                </Text>
              </Box>
            )}
            <ConversationMessages key={"ConversationMessages:" + active}>
              {!!thread.delivery_activity?.length && (
                <Box as="details" borderWidth="1px" borderRadius="md" p={3} fontSize="xs">
                  <summary>Message delivery activity</summary>
                  <Text my={2}>Ticks show confirmed sent, delivered and read status. Missing receipts remain unconfirmed. App delivery means the conversation was fetched; it does not mean it was read.</Text>
                  {thread.delivery_activity.map((delivery: any) => (
                    <Box key={delivery.id} py={2} borderTopWidth="1px">
                      <Text fontWeight="bold">
                        {atTime(delivery.created_at)} · {({web: "Website", mobile: "Mobile app", whatsapp: "WhatsApp"} as Record<string, string>)[delivery.channel] || delivery.channel} · {conversationDelivery(delivery)}
                      </Text>
                      <Text noOfLines={2}>{delivery.text}</Text>
                    </Box>
                  ))}
                </Box>
              )}
              {(!thread.thread || thread.thread.length === 0) && (
                <Text color="admin.muted" textAlign="center" mt={6}>
                  No messages in this conversation yet.
                </Text>
              )}
              {(thread.thread || []).map((m: any, i: number) => {
                const s = KIND_STYLE[m.kind] || KIND_STYLE.event;
                // event items carry an OBJECT {icon, label, raw}; text items carry
                // a string. Rendering the object directly crashes React.
                const isEvent = m.kind === "event";
                const label = isEvent
                  ? `${m.event?.icon || "•"} ${m.event?.label || ""}`.trim()
                  : "";
                return (
                  <Flex key={m.image_message_id ? `${thread.phone_number}:image:${m.image_message_id}` : `${thread.phone_number}:message:${i}`} justify={s.align}>
                    <Box maxW="70%" bg={s.bg} px={3} py={2} borderRadius="lg"
                      fontStyle={isEvent ? "italic" : "normal"}>
                      {s.label && (
                        <Flex gap={2} align="baseline" justify="space-between">
                          <Text fontSize="0.6em" color="admin.muted">
                            {m.kind === "user" ? partyLabel(thread?.party) : s.label}
                          </Text>
                          {atTime(m.ts) && (
                            <Text fontSize="0.6em" color="admin.muted" whiteSpace="nowrap">
                              {atTime(m.ts)}{["agent", "human"].includes(m.kind) && <MessageReceipt status={conversationReceipt(m, thread.delivery_activity)} />}
                            </Text>
                          )}
                        </Flex>
                      )}
                      {m.image_message_id && <ConversationImage phone={thread.phone_number} messageId={m.image_message_id} />}
                      <Text fontSize="sm" whiteSpace="pre-wrap">
                        {isEvent ? label : m.image_description ? m.image_caption : (typeof m.text === "string" ? m.text : "")}
                      </Text>
                      {m.image_description && <Box as="details" mt={2} fontSize="sm">
                        <summary>AI interpretation of photo</summary>
                        <Text mt={2} whiteSpace="pre-wrap">{m.image_description}</Text>
                      </Box>}
                    </Box>
                  </Flex>
                );
              })}
            </ConversationMessages>
            <Flex p={3} borderTopWidth="1px" gap={2}>
              <Textarea aria-label="Reply to conversation" size="sm" rows={1} placeholder="Message the customer directly…" value={draft}
                onChange={(e) => { const value = e.target.value; setDrafts(previous => ({...previous, [active]: value})); }}
                onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey && !e.nativeEvent.isComposing) { e.preventDefault(); send(); } }} />
              <Button colorScheme="purple" backgroundColor="#88077b" onClick={send} isLoading={sendingPhones.includes(active)} isDisabled={!draft.trim()}>Send</Button>
            </Flex>
          </>
        ) : (
          <Flex flex="1" align="center" justify="center" direction="column" gap={3}>
            <Text color="admin.muted">Could not load this conversation.</Text>
            <Button onClick={() => openThread(active)} colorScheme="purple">Retry conversation</Button>
          </Flex>
        )}
      </Box>
    </Flex>
  );
};
export default Conversations;
